import React from 'react';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { Flex } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder';
import { RelayMigrationProvider } from '@atlassian/jira-relay-apollo-migration-utils';
import type { insightsDevelopmentQuery } from '@atlassian/jira-relay/src/__generated__/insightsDevelopmentQuery.graphql.ts';
import type { uiPeriscopeQuery } from '@atlassian/jira-relay/src/__generated__/uiPeriscopeQuery.graphql';
import { PreconditionCheck } from '@atlassian/jira-spa-apps-software-reports-cycle-time-report/src/ui/precondition';
import { useRouter } from '@atlassian/react-resource-router';
import { WorkSuggestionsStateContainer } from '../services/periscope-container';
import { Activity } from './activity';
import { Header } from './header';
import { InsightsPanel } from './insights';
import { InsightsSkeleton, SuggestionsSkeleton } from './skeleton';
import { SuggestionsPanel } from './suggestions';

export const Periscope = ({
	queryReference,
	insightsDevelopmentQueryRef,
}: {
	queryReference: PreloadedQuery<uiPeriscopeQuery>;
	insightsDevelopmentQueryRef: PreloadedQuery<insightsDevelopmentQuery> | null | undefined;
}) => {
	const [{ match }] = useRouter();
	const projectKey = match.params.projectKey ?? '';
	const data = usePreloadedQuery<uiPeriscopeQuery>(
		graphql`
			query uiPeriscopeQuery(
				$cloudId: ID!
				$projectAris: [ID!]!
				$first: Int
				$after: String
				$sprintAutoDiscoveryLimit: Int
				$isAutoDevJobsSuggestionEnabled: Boolean!
				$isPullRequestSuggestionsEnabled: Boolean!
				$isCompassSuggestionsEnabled: Boolean!
			) @preloadable {
				...suggestions_periscope_SuggestionsPanel_fragmentRef
					@arguments(
						cloudId: $cloudId
						projectAris: $projectAris
						first: $first
						after: $after
						sprintAutoDiscoveryLimit: $sprintAutoDiscoveryLimit
						isAutoDevJobsSuggestionEnabled: $isAutoDevJobsSuggestionEnabled
						isPullRequestSuggestionsEnabled: $isPullRequestSuggestionsEnabled
						isCompassSuggestionsEnabled: $isCompassSuggestionsEnabled
					)
			}
		`,
		queryReference,
	);

	return (
		<WorkSuggestionsStateContainer
			projectAris={queryReference.variables.projectAris}
			sprintAutoDiscoveryLimit={queryReference.variables.sprintAutoDiscoveryLimit}
		>
			<Flex direction="column" alignItems="start">
				<Header />
				{fg('jira_periscope_insights_panel') && (
					<Placeholder name="periscope-page-insights" fallback={<InsightsSkeleton />}>
						{insightsDevelopmentQueryRef && (
							<InsightsPanel insightsDevelopmentQueryRef={insightsDevelopmentQueryRef} />
						)}
					</Placeholder>
				)}
				<Placeholder name="periscope-page-suggestions" fallback={<SuggestionsSkeleton />}>
					<SuggestionsPanel fragmentRef={data} />
				</Placeholder>
				{fg('jira-jsw-development-page-activity-chart') && (
					<PreconditionCheck projectKey={projectKey}>
						{(hasProvider) =>
							hasProvider ? (
								<RelayMigrationProvider>
									<Activity />
								</RelayMigrationProvider>
							) : (
								<></>
							)
						}
					</PreconditionCheck>
				)}
			</Flex>
		</WorkSuggestionsStateContainer>
	);
};
